import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs,
  doc,
  getDoc 
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { 
  ClipboardDocumentListIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  UserCircleIcon,
  BellIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const SupplierDashboard = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardData, setDashboardData] = useState({
    activeQuotes: [],
    activeJobs: [],
    completedProjects: [],
    performanceMetrics: null,
    notifications: [],
    queries: []
  });

  useEffect(() => {
    loadSupplierDashboard();
  }, [token]);

  const loadSupplierDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🚀 Starting dashboard load for token:', token);
      
      // First, validate the token and get supplier info
      const supplierInfo = await validateTokenAndGetSupplier(token);
      if (!supplierInfo) {
        setError('Invalid or expired access token. Please check your link.');
        return;
      }
      
      console.log('✅ Supplier info validated:', supplierInfo.name, supplierInfo.email);
      setSupplier(supplierInfo);
      
      // Load all dashboard data
      console.log('📊 Loading dashboard data...');
      const [quotes, jobs, completed, metrics, notifications, queries] = await Promise.all([
        loadActiveQuotes(supplierInfo.email),
        loadActiveJobs(supplierInfo.email),
        loadCompletedProjects(supplierInfo.email),
        loadPerformanceMetrics(supplierInfo.email),
        loadNotifications(supplierInfo.email),
        loadQueries(supplierInfo.email)
      ]);

      const dashboardState = {
        activeQuotes: quotes,
        activeJobs: jobs,
        completedProjects: completed,
        performanceMetrics: metrics,
        notifications,
        queries
      };
      
      console.log('🎉 Dashboard data loaded successfully:', {
        activeQuotes: quotes.length,
        activeJobs: jobs.length,
        completedProjects: completed.length,
        hasMetrics: !!metrics,
        notifications: notifications.length,
        queries: queries.length
      });
      
      setDashboardData(dashboardState);
      
    } catch (error) {
      console.error('❌ Critical error loading supplier dashboard:', error);
      setError(`Failed to load dashboard: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const validateTokenAndGetSupplier = async (token) => {
    try {
      console.log('🔐 Validating token:', token);
      
      // Check if token exists in quoteRequests
      const quoteRequestsQuery = query(
        collection(db, 'quoteRequests'),
        where('token', '==', token)
      );
      const quoteRequestsSnap = await getDocs(quoteRequestsQuery);
      
      console.log('📋 Found', quoteRequestsSnap.docs.length, 'quote requests for token');
      
      if (!quoteRequestsSnap.empty) {
        const quoteData = quoteRequestsSnap.docs[0].data();
        console.log('✅ Quote request found for supplier:', quoteData.supplierName);
        return {
          email: quoteData.supplierEmail,
          name: quoteData.supplierName,
          id: quoteData.supplierId,
          source: 'quote'
        };
      }

      // Check if token exists in jobSheets
      const jobSheetsQuery = query(
        collection(db, 'jobSheets'),
        where('referenceNumber', '==', token)
      );
      const jobSheetsSnap = await getDocs(jobSheetsQuery);
      
      console.log('📄 Found', jobSheetsSnap.docs.length, 'job sheets for token');
      
      if (!jobSheetsSnap.empty) {
        const jobData = jobSheetsSnap.docs[0].data();
        console.log('✅ Job sheet found for supplier:', jobData.supplierName);
        return {
          email: jobData.supplierEmail,
          name: jobData.supplierName,
          id: jobData.supplierId,
          source: 'job'
        };
      }

      console.warn('⚠️ No valid token found in either collection');
      return null;
    } catch (error) {
      console.error('❌ Error validating token:', error);
      return null;
    }
  };

  const loadActiveQuotes = async (supplierEmail) => {
    try {
      console.log('🔍 Loading active quotes for:', supplierEmail);
      
      // First get all quotes for this supplier without orderBy to avoid index issues
      const q = query(
        collection(db, 'quoteRequests'),
        where('supplierEmail', '==', supplierEmail),
        where('status', 'in', ['pending', 'accessed', 'submitted'])
      );
      const snapshot = await getDocs(q);
      
      console.log('📊 Found', snapshot.docs.length, 'active quotes');
      
      const quotes = [];
      for (const docSnap of snapshot.docs) {
        const quoteData = { id: docSnap.id, ...docSnap.data() };
        
        console.log('📝 Processing quote:', quoteData.taskName, 'Status:', quoteData.status);
        
        // Get project details
        try {
          const projectDoc = await getDoc(doc(db, 'projects', quoteData.projectId));
          if (projectDoc.exists()) {
            quoteData.project = projectDoc.data();
          } else {
            console.warn('⚠️ Project not found:', quoteData.projectId);
          }
        } catch (projectError) {
          console.error('❌ Error loading project:', projectError);
        }
        
        quotes.push(quoteData);
      }
      
      // Sort by createdAt in memory (newest first)
      quotes.sort((a, b) => {
        const aDate = a.createdAt?.seconds || a.invitedAt?.seconds || 0;
        const bDate = b.createdAt?.seconds || b.invitedAt?.seconds || 0;
        return bDate - aDate;
      });
      
      console.log('✅ Loaded', quotes.length, 'active quotes successfully');
      return quotes;
    } catch (error) {
      console.error('❌ Error loading active quotes:', error);
      return [];
    }
  };

  const loadActiveJobs = async (supplierEmail) => {
    try {
      console.log('🔍 Loading active jobs for:', supplierEmail);
      
      // Get all job sheets without orderBy to avoid index issues
      const q = query(
        collection(db, 'jobSheets'),
        where('supplierEmail', '==', supplierEmail),
        where('status', 'in', ['sent', 'acknowledged', 'in_progress'])
      );
      const snapshot = await getDocs(q);
      
      console.log('📊 Found', snapshot.docs.length, 'active jobs');
      
      const jobs = [];
      for (const docSnap of snapshot.docs) {
        const jobData = { id: docSnap.id, ...docSnap.data() };
        
        console.log('📝 Processing job:', jobData.referenceNumber, 'Status:', jobData.status);
        
        // Get project details
        try {
          const projectDoc = await getDoc(doc(db, 'projects', jobData.projectId));
          if (projectDoc.exists()) {
            jobData.project = projectDoc.data();
          } else {
            console.warn('⚠️ Project not found for job:', jobData.projectId);
          }
        } catch (projectError) {
          console.error('❌ Error loading project for job:', projectError);
        }
        
        jobs.push(jobData);
      }
      
      // Sort by sentAt in memory (newest first)
      jobs.sort((a, b) => {
        const aDate = a.sentAt?.seconds || 0;
        const bDate = b.sentAt?.seconds || 0;
        return bDate - aDate;
      });
      
      console.log('✅ Loaded', jobs.length, 'active jobs successfully');
      return jobs;
    } catch (error) {
      console.error('❌ Error loading active jobs:', error);
      return [];
    }
  };

  const loadCompletedProjects = async (supplierEmail) => {
    try {
      const quotesQuery = query(
        collection(db, 'quoteRequests'),
        where('supplierEmail', '==', supplierEmail),
        where('status', '==', 'accepted'),
        orderBy('updatedAt', 'desc')
      );
      const quotesSnapshot = await getDocs(quotesQuery);
      
      const completed = [];
      for (const doc of quotesSnapshot.docs) {
        const quoteData = { id: doc.id, ...doc.data() };
        
        // Get project details
        const projectDoc = await getDoc(doc(db, 'projects', quoteData.projectId));
        if (projectDoc.exists()) {
          quoteData.project = projectDoc.data();
          completed.push(quoteData);
        }
      }
      
      return completed.slice(0, 10); // Show last 10 completed
    } catch (error) {
      console.error('Error loading completed projects:', error);
      return [];
    }
  };

  const loadPerformanceMetrics = async (supplierEmail) => {
    try {
      console.log('🔍 Loading performance metrics for:', supplierEmail);
      
      // Get all quotes for this supplier
      const allQuotesQuery = query(
        collection(db, 'quoteRequests'),
        where('supplierEmail', '==', supplierEmail)
      );
      const allQuotesSnapshot = await getDocs(allQuotesQuery);
      
      const quotes = allQuotesSnapshot.docs.map(doc => doc.data());
      const totalQuotes = quotes.length;
      const acceptedQuotes = quotes.filter(q => q.status === 'accepted').length;
      const submittedQuotes = quotes.filter(q => q.status === 'submitted' || q.status === 'accepted').length;
      
      console.log('📊 Performance stats:', { totalQuotes, submittedQuotes, acceptedQuotes });
      
      // Calculate response time (average time to submit) - handle both Firestore timestamps and regular dates
      const responseTimes = quotes
        .filter(q => q.submittedAt && (q.createdAt || q.invitedAt))
        .map(q => {
          const submitted = q.submittedAt.seconds || (new Date(q.submittedAt).getTime() / 1000);
          const created = (q.createdAt?.seconds || q.invitedAt?.seconds) || (new Date(q.createdAt || q.invitedAt).getTime() / 1000);
          return (submitted - created) / 3600; // in hours
        });
      
      const avgResponseTime = responseTimes.length > 0 
        ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length 
        : 0;

      const metrics = {
        totalQuotes,
        submittedQuotes,
        acceptedQuotes,
        successRate: totalQuotes > 0 ? Math.round((acceptedQuotes / totalQuotes) * 100) : 0,
        responseRate: totalQuotes > 0 ? Math.round((submittedQuotes / totalQuotes) * 100) : 0,
        avgResponseTime: Math.max(0, Math.round(avgResponseTime))
      };
      
      console.log('✅ Performance metrics calculated:', metrics);
      return metrics;
    } catch (error) {
      console.error('❌ Error loading performance metrics:', error);
      return null;
    }
  };

  const loadNotifications = async (supplierEmail) => {
    try {
      console.log('🔍 Loading notifications for:', supplierEmail);
      
      const q = query(
        collection(db, 'notifications'),
        where('recipientEmail', '==', supplierEmail)
      );
      const snapshot = await getDocs(q);
      
      const notifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort in memory by createdAt (newest first)
      notifications.sort((a, b) => {
        const aDate = a.createdAt?.seconds || 0;
        const bDate = b.createdAt?.seconds || 0;
        return bDate - aDate;
      });
      
      console.log('📊 Found', notifications.length, 'notifications');
      return notifications.slice(0, 10);
    } catch (error) {
      console.error('❌ Error loading notifications:', error);
      return [];
    }
  };

  const loadQueries = async (supplierEmail) => {
    try {
      console.log('🔍 Loading queries for:', supplierEmail);
      
      const q = query(
        collection(db, 'supplierQueries'),
        where('supplierEmail', '==', supplierEmail)
      );
      const snapshot = await getDocs(q);
      
      const queries = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort in memory by createdAt (newest first)
      queries.sort((a, b) => {
        const aDate = a.createdAt?.seconds || 0;
        const bDate = b.createdAt?.seconds || 0;
        return bDate - aDate;
      });
      
      console.log('📊 Found', queries.length, 'supplier queries');
      return queries.slice(0, 5);
    } catch (error) {
      console.error('❌ Error loading queries:', error);
      return [];
    }
  };

  const getStatusBadge = (status, type = 'quote') => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon, label: 'Pending' },
      accessed: { color: 'bg-blue-100 text-blue-800', icon: EyeIcon, label: 'Viewed' },
      submitted: { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon, label: 'Submitted' },
      accepted: { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon, label: 'Accepted' },
      rejected: { color: 'bg-red-100 text-red-800', icon: ExclamationTriangleIcon, label: 'Not Selected' },
      sent: { color: 'bg-purple-100 text-purple-800', icon: DocumentTextIcon, label: 'Sent' },
      acknowledged: { color: 'bg-blue-100 text-blue-800', icon: CheckCircleIcon, label: 'Acknowledged' },
      in_progress: { color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon, label: 'In Progress' },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon, label: 'Completed' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">Please contact Pink Flamingo Interiors if you continue to have access issues.</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'quotes', name: 'Active Quotes', icon: ClipboardDocumentListIcon, count: dashboardData.activeQuotes.length },
    { id: 'jobs', name: 'Active Jobs', icon: DocumentTextIcon, count: dashboardData.activeJobs.length },
    { id: 'history', name: 'History', icon: CalendarDaysIcon },
    { id: 'messages', name: 'Messages', icon: ChatBubbleLeftRightIcon, count: dashboardData.queries.length },
    { id: 'profile', name: 'Profile', icon: UserCircleIcon }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <h1 className="text-xl font-bold text-gray-900">
                  Supplier Portal
                </h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{supplier?.name}</p>
                <p className="text-xs text-gray-500">{supplier?.email}</p>
              </div>
              <div className="relative">
                <BellIcon className="h-6 w-6 text-gray-400" />
                {dashboardData.notifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full"></span>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-pink-500 text-pink-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-2" />
                  {tab.name}
                  {tab.count > 0 && (
                    <span className="ml-2 bg-pink-100 text-pink-600 py-0.5 px-2 rounded-full text-xs">
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Performance Metrics */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Performance</h3>
              {dashboardData.performanceMetrics && dashboardData.performanceMetrics.totalQuotes > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {dashboardData.performanceMetrics.totalQuotes}
                    </div>
                    <div className="text-sm text-blue-600">Total Quotes</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {dashboardData.performanceMetrics.successRate}%
                    </div>
                    <div className="text-sm text-green-600">Success Rate</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">
                      {dashboardData.performanceMetrics.responseRate}%
                    </div>
                    <div className="text-sm text-purple-600">Response Rate</div>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">
                      {dashboardData.performanceMetrics.avgResponseTime}h
                    </div>
                    <div className="text-sm text-orange-600">Avg Response</div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <ChartBarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">Performance metrics will appear here</p>
                  <p className="text-gray-400 text-xs mt-1">Complete some quote requests to see your performance statistics.</p>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Quotes</h3>
                {dashboardData.activeQuotes.length > 0 ? (
                  <div className="space-y-3">
                    {dashboardData.activeQuotes.slice(0, 3).map((quote) => (
                      <div key={quote.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{quote.project?.name}</p>
                          <p className="text-xs text-gray-500">{quote.taskName}</p>
                        </div>
                        <div className="text-right">
                          {getStatusBadge(quote.status)}
                          <p className="text-xs text-gray-500 mt-1">{formatDate(quote.createdAt)}</p>
                        </div>
                      </div>
                    ))}
                    {dashboardData.activeQuotes.length > 3 && (
                      <button 
                        onClick={() => setActiveTab('quotes')}
                        className="text-sm text-pink-600 hover:text-pink-700 font-medium"
                      >
                        View all {dashboardData.activeQuotes.length} quotes →
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ClipboardDocumentListIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">No active quote requests</p>
                    <p className="text-gray-400 text-xs mt-1">New quote requests will appear here when Pink Flamingo Interiors invites you to submit quotes.</p>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Jobs</h3>
                {dashboardData.activeJobs.length > 0 ? (
                  <div className="space-y-3">
                    {dashboardData.activeJobs.slice(0, 3).map((job) => (
                      <div key={job.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{job.projectDetails?.name}</p>
                          <p className="text-xs text-gray-500">{job.taskDetails?.name}</p>
                        </div>
                        <div className="text-right">
                          {getStatusBadge(job.status, 'job')}
                          <p className="text-xs text-gray-500 mt-1">{formatDate(job.sentAt)}</p>
                        </div>
                      </div>
                    ))}
                    {dashboardData.activeJobs.length > 3 && (
                      <button 
                        onClick={() => setActiveTab('jobs')}
                        className="text-sm text-pink-600 hover:text-pink-700 font-medium"
                      >
                        View all {dashboardData.activeJobs.length} jobs →
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <DocumentTextIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">No active job sheets</p>
                    <p className="text-gray-400 text-xs mt-1">Job sheets for work assignments will appear here when you have active projects.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
              {dashboardData.notifications.length > 0 ? (
                <div className="space-y-3">
                  {dashboardData.notifications.slice(0, 5).map((notification) => (
                    <div key={notification.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm text-gray-900">{notification.message}</p>
                        <p className="text-xs text-gray-500 mt-1">{formatDate(notification.createdAt)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No recent activity</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'quotes' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Active Quote Requests</h3>
              </div>
              <div className="divide-y divide-gray-200">
                {dashboardData.activeQuotes.length > 0 ? (
                  dashboardData.activeQuotes.map((quote) => (
                    <div key={quote.id} className="p-6 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="text-lg font-medium text-gray-900">{quote.project?.name}</h4>
                            {getStatusBadge(quote.status)}
                          </div>
                          <p className="text-gray-600 mb-2">{quote.taskName}</p>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span>Created: {formatDate(quote.createdAt)}</span>
                            {quote.deadline && <span>Due: {formatDate(quote.deadline)}</span>}
                          </div>
                        </div>
                        <div className="ml-4">
                          <Link
                            to={`/quote/${quote.token}`}
                            className="bg-pink-600 text-white px-4 py-2 rounded-lg hover:bg-pink-700 transition-colors"
                          >
                            {quote.status === 'pending' ? 'Submit Quote' : 'View Details'}
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-6 text-center text-gray-500">
                    <ClipboardDocumentListIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No active quote requests</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'jobs' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Active Job Sheets</h3>
              </div>
              <div className="divide-y divide-gray-200">
                {dashboardData.activeJobs.length > 0 ? (
                  dashboardData.activeJobs.map((job) => (
                    <div key={job.id} className="p-6 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="text-lg font-medium text-gray-900">{job.projectDetails?.name}</h4>
                            {getStatusBadge(job.status, 'job')}
                          </div>
                          <p className="text-gray-600 mb-2">{job.taskDetails?.name}</p>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span>Sent: {formatDate(job.sentAt)}</span>
                            <span>Reference: {job.referenceNumber}</span>
                            {job.acknowledgmentDeadline && (
                              <span>Acknowledge by: {formatDate(job.acknowledgmentDeadline)}</span>
                            )}
                          </div>
                        </div>
                        <div className="ml-4">
                          <Link
                            to={`/job-sheet/${job.id}`}
                            className="bg-pink-600 text-white px-4 py-2 rounded-lg hover:bg-pink-700 transition-colors"
                          >
                            View Job Sheet
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-6 text-center text-gray-500">
                    <DocumentTextIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No active job sheets</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Completed Projects</h3>
              </div>
              <div className="divide-y divide-gray-200">
                {dashboardData.completedProjects.length > 0 ? (
                  dashboardData.completedProjects.map((project) => (
                    <div key={project.id} className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="text-lg font-medium text-gray-900">{project.project?.name}</h4>
                            {getStatusBadge('accepted')}
                          </div>
                          <p className="text-gray-600 mb-2">{project.taskName}</p>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span>Completed: {formatDate(project.updatedAt)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-6 text-center text-gray-500">
                    <CalendarDaysIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No completed projects yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'messages' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Recent Messages & Queries</h3>
              </div>
              <div className="divide-y divide-gray-200">
                {dashboardData.queries.length > 0 ? (
                  dashboardData.queries.map((query) => (
                    <div key={query.id} className="p-6">
                      <div className="space-y-3">
                        <div className="flex items-start space-x-3">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">Your Question:</p>
                            <p className="text-gray-700 mt-1">{query.question}</p>
                          </div>
                          <span className="text-xs text-gray-500">{formatDate(query.createdAt)}</span>
                        </div>
                        {query.response && (
                          <div className="bg-pink-50 p-3 rounded-lg">
                            <p className="text-sm font-medium text-pink-900">PFI Response:</p>
                            <p className="text-pink-800 mt-1">{query.response}</p>
                            {query.respondedAt && (
                              <span className="text-xs text-pink-600 mt-2 block">
                                Responded: {formatDate(query.respondedAt)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-6 text-center text-gray-500">
                    <ChatBubbleLeftRightIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No messages yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Supplier Profile</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Company Name</label>
                  <p className="mt-1 text-gray-900">{supplier?.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="mt-1 text-gray-900">{supplier?.email}</p>
                </div>
                <div className="pt-4">
                  <p className="text-sm text-gray-500">
                    To update your profile information, please contact Pink Flamingo Interiors directly.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierDashboard;